import MuteButton from "@assets/quick-news/Mute.png";
import PlayIcon from "@assets/quick-news/play-icon.png";
import ShareIcon from "@assets/quick-news/share-light.svg";
import UnMuteButton from "@assets/quick-news/Unmute.png";
import { QnBrightcoveVideo } from "@components/Blocks/BrightcoveVideo/QnBrightcoveVideo";
import { QN_SOCIAL_ICON_LIST } from "@config/quickNews";
import { Image } from "@elements/ResponsiveImage/Image";
import { BC_PLAYER_ID_QUICK_NEWS } from "@helper/getEnvVariables";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { ShareSocialBar } from "@src/app/routes/Article/Components/ShareSocialBar";
import { type LatestVideoResponse } from "@transformer/useBrightcoveResponse";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type PlayerProps = {
  bcVideo: LatestVideoResponse;
  isMute: boolean;
  setIsMute: (mute: boolean) => void;
  isActive?: boolean;
  videoIndex?: number;
};

const PLAY_ICON_SIZE = 60;
const PLAY_ICON_OFFSET = PLAY_ICON_SIZE / 2;

const QnVideoCard = ({
  bcVideo,
  isMute,
  setIsMute,
  isActive = false,
  videoIndex = 0,
}: PlayerProps) => {
  const playerId = BC_PLAYER_ID_QUICK_NEWS;
  const containerRef = useRef<HTMLDivElement>(null);

  const BHCustomEvent = useBHCustomEventAnalyticsCall();

  const [expanded, setExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleMute = useCallback(() => {
    setIsMute(!isMute);
  }, [isMute, setIsMute]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const playIconStyle = useMemo(
    () => ({
      width: PLAY_ICON_SIZE,
      height: PLAY_ICON_SIZE,
      left: `calc(50% - ${PLAY_ICON_OFFSET}px)`,
      top: `calc(50% - ${PLAY_ICON_OFFSET}px)`,
    }),
    [],
  );

  if (!bcVideo.id) return <></>;

  return (
    <div className="h-full w-full bg-transparent">
      {/* VIDEO CONTAINER */}
      <div className="relative mx-auto h-full w-full items-center justify-center overflow-hidden">
        {/* Video Player */}
        {isActive && (
          <QnBrightcoveVideo
            videoId={bcVideo.id}
            forwardedRef={containerRef}
            className="quick-news-video !aspect-[167/297]"
            playerId={playerId}
            customButtons
            isMute={isMute}
            setIsMute={setIsMute}
            onPlay={handlePlay}
            onPause={handlePause}
          />
        )}

        {/* Poster Fallback */}
        <img
          src={bcVideo.poster}
          alt={bcVideo.name}
          loading="lazy"
          className={twMerge(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
            isActive && "opacity-0",
          )}
        />

        {/* Play Icon */}
        {!isPlaying && (
          <div
            className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-100"
            style={playIconStyle}
          >
            <img
              src={PlayIcon}
              alt="Play Video"
              width={PLAY_ICON_SIZE}
              height={PLAY_ICON_SIZE}
            />
          </div>
        )}
      </div>

      {/* TEXT + ACTIONS OVERLAY */}
      <div className="absolute bottom-0 z-30 w-full bg-gradient-to-t from-neutral-1000/60 via-neutral-1000/30 to-transparent p-3">
        <Heading
          Element="h1"
          className="font-primary text-[22px] font-semibold leading-650 text-text-inverse"
        >
          <button
            onClick={toggleExpanded}
            className={twMerge(
              "w-full cursor-pointer text-left transition-all duration-300",
              expanded ? "line-clamp-none" : "line-clamp-2",
            )}
            title={`Tap to ${expanded ? "collapse" : "expand"}`}
          >
            {bcVideo.name}
          </button>
        </Heading>

        <div className="actions mt-auto flex items-start justify-between gap-2 px-2 py-2">
          {/* Mute/Unmute */}
          <button
            onClick={toggleMute}
            className="relative my-auto w-[30px] rounded"
          >
            <Image
              className="h-[17px] w-[25px]"
              src={isMute ? MuteButton : UnMuteButton}
              alt={isMute ? "Mute" : "Unmute"}
              srcWidth={22}
              srcHeight={17}
            />
          </button>

          {/* Social Share */}
          <div className="ml-auto flex items-end">
            <ShareSocialBar
              dateCreated={bcVideo.formattedTime}
              pageUrl={`/quick-news/video/${bcVideo.id}`}
              socialIconList={QN_SOCIAL_ICON_LIST}
              title={bcVideo.name}
              renderButton={(onClick) => (
                <button onClick={onClick}>
                  <img src={ShareIcon} alt="Share" width={24} height={24} />
                </button>
              )}
              onClickGaEvent={() => {
                BHCustomEvent(
                  "quick-news",
                  `share::${videoIndex + 1}`,
                  bcVideo.name,
                  undefined,
                  "",
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

QnVideoCard.displayName = "QnVideoCard";

export default memo(QnVideoCard);
